import _ from 'lodash'
import type { QueryProperty, Component, BaseDataItem, DataMapName } from '@wix/thunderbolt-becky-types'
import { removeHash } from '@wix/thunderbolt-catharsis'
import { QUERY_MAPPING, RESOLVED_TRANSLATIONS_ASPECT } from '../constants'
import type { ScopedCreateRef } from '../compNodeUtils'
import type {
	GetOverrideDataRefs,
	GetOverrideTranslationDataRefs,
	GetLeafsOverridesMapsRefs,
	GetRefCompResolvedPresetsRef,
	DataRefsForPresetResolving,
	DataItemWithOverridesRefs,
	DataItemWithDependencies,
} from './types'

const queryNames = Object.keys(QUERY_MAPPING) as Array<QueryProperty>

export const getDataItemRefsWithOverrides = ({
	templateComponent,
	queryName,
	itemSuffix = '',
	createRefInTemplateNamespace,
	getOverrideDataRefs,
}: {
	templateComponent: Component
	queryName: QueryProperty
	itemSuffix?: string
	createRefInTemplateNamespace: ScopedCreateRef
	getOverrideDataRefs: GetOverrideDataRefs
}) => ({
	dataItemWithOverrides: [
		...getOverrideDataRefs(templateComponent.id, queryName, itemSuffix),
		templateComponent[queryName] &&
			createRefInTemplateNamespace<BaseDataItem>(
				QUERY_MAPPING[queryName],
				removeHash(templateComponent[queryName])
			),
	],
})

const getDataAndTranslationItemRefsWithOverrides = ({
	templateComponent,
	queryName,
	itemSuffix = '',
	createRefInTemplateNamespace,
	getOverrideDataRefs,
	getOverrideTranslationDataRefs,
}: {
	templateComponent: Component
	queryName: QueryProperty
	itemSuffix?: string
	createRefInTemplateNamespace: ScopedCreateRef
	getOverrideDataRefs: GetOverrideDataRefs
	getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs
}) => ({
	...getDataItemRefsWithOverrides({
		templateComponent,
		queryName,
		itemSuffix,
		createRefInTemplateNamespace,
		getOverrideDataRefs,
	}),
	translationDataItemWithOverrides: [
		...getOverrideTranslationDataRefs!(templateComponent.id),
		createRefInTemplateNamespace<BaseDataItem>(
			RESOLVED_TRANSLATIONS_ASPECT,
			removeHash(templateComponent[queryName])
		),
	],
})

export const createLayoutAndStylePresetsDependenciesResolver = ({
	createRefInTemplateNamespace,
	getOverrideDataRefs,
	getLeafsOverridesMapsRefs,
	getParentRefCompResolvedPresets,
}: {
	createRefInTemplateNamespace: ScopedCreateRef
	getOverrideDataRefs: GetOverrideDataRefs
	getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs
	getParentRefCompResolvedPresets: GetRefCompResolvedPresetsRef
}) => (templateComponent: Component) =>
	_.reduce(
		['layoutQuery', 'styleId'] as const,
		(acc, queryName) => {
			if (!templateComponent[queryName]) {
				return acc
			}

			const { dataItemWithOverrides: refArraysWithOverrides } = getDataItemRefsWithOverrides({
				templateComponent,
				queryName,
				createRefInTemplateNamespace,
				getOverrideDataRefs,
			})

			const leafsOverridesMaps = getLeafsOverridesMapsRefs(templateComponent.id, queryName)
			const parentRefCompResolvedPresets = getParentRefCompResolvedPresets()

			acc[queryName] = {
				refArraysWithOverrides,
				leafsOverridesMaps,
				parentRefCompResolvedPresets,
			}

			return acc
		},
		{} as Record<'layoutQuery' | 'styleId', DataRefsForPresetResolving>
	)

export const createOverrideDataItemsResolver = ({
	createRefInTemplateNamespace,
	getOverrideDataRefs,
	getOverrideTranslationDataRefs,
}: {
	createRefInTemplateNamespace: ScopedCreateRef
	getOverrideDataRefs: GetOverrideDataRefs
	getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs
}) => (templateComponent: Component) =>
	_.reduce(
		queryNames,
		(acc, queryName) => {
			if (
				!templateComponent[queryName] ||
				queryName === 'propertyQuery' ||
				queryName === 'layoutQuery' ||
				queryName === 'styleId' ||
				queryName === 'presetsQuery'
			) {
				return acc
			}

			const dataMapName = QUERY_MAPPING[queryName]

			if (dataMapName === 'document_data') {
				acc[dataMapName] = getDataAndTranslationItemRefsWithOverrides({
					templateComponent,
					queryName,
					createRefInTemplateNamespace,
					getOverrideDataRefs,
					getOverrideTranslationDataRefs,
				})

				return acc
			}

			acc[dataMapName] = getDataItemRefsWithOverrides({
				templateComponent,
				queryName,
				createRefInTemplateNamespace,
				getOverrideDataRefs,
			})

			return acc
		},
		{} as Record<DataMapName, DataItemWithOverridesRefs>
	)

// get the most external override (which is the determining one). If non exist, returns the default data item
export const resolveOverrideItem = (
	items: Readonly<Array<DataItemWithDependencies | undefined>>,
	inflatedDataItemId: string
): DataItemWithDependencies => {
	const dataItem = items.find(Boolean)!
	return {
		...dataItem,
		id: inflatedDataItemId,
	}
}

export const getResolvedOverrideItemNestingLevel = (items: Readonly<Array<BaseDataItem | undefined>>) =>
	items.findIndex(Boolean)!
